import React, { Fragment, useEffect, useRef, useState } from 'react';
import { updatePreferencesApi } from '../../api';
import { useSelector, shallowEqual } from 'react-redux';
import {
  Modal,
  Button,
  Dropdown,
  Form,
  Icon,
  Input,
  Menu,
  Message,
} from 'semantic-ui-react';
import * as Yup from 'yup';
import SemanticField from '../../components/controls/SemanticField';
import additionalMessages from '../../messages';
import {
  LENGTH_OPTIONS,
  HEIGHT_OPTIONS,
  MAC_ADDRESS_OPTIONS,
  LAT_LNG_OPTIONS,
  LANGUAGES_PREFERENCES,
  LINE_STYLE_OPTIONS,
  getDashArray,
  getLineCap,
  LINE_THICKNESS_MIN,
  LINE_THICKNESS_MAX,
  LINE_THICKNESS_STEP,
  MAP_REGIONS,
  DEFAULT_GRAPHICS_PREFS,
} from '../../app.constants';
import { trimObject } from '../../utils/useful_functions';
import { Formik, Form as FormFormik, useField, useFormikContext } from 'formik';
import { store } from '../../store';
import {
  uiToggleHelpPopups,
  updatePreferences,
} from '../mainframe/mainframe.reducer';
import { convertObjectKeys, toSnake } from '../../utils/change-case';
import OrderableCheckListControl from 'src/components/controls/OrderableCheckListControl';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { getCanvas } from '../../utils/mapUtils';

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const GRAPHICS_ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '4px 0',
};
const PREVIEW_WIDTH = 48;
const COLOR_SWATCH_STYLE = {
  width: '34px',
  height: '26px',
  padding: 0,
  border: '1px solid rgba(34,36,38,.15)',
  borderRadius: '3px',
  cursor: 'pointer',
  background: 'none',
};
const HEX_INPUT_STYLE = { width: '95px' };
const LINE_STYLE_DROPDOWN_STYLE = { width: '110px' };
const THICKNESS_SLIDER_STYLE = { width: '110px' };

function SitePreview({ color }) {
  return (
    <svg width="20" height="20">
      <circle
        cx="10"
        cy="10"
        r="6"
        fill={color}
        stroke="#000"
        strokeWidth="1"
      />
    </svg>
  );
}

function LinePreview({ color, lineStyle, weight }) {
  return (
    <svg width={PREVIEW_WIDTH} height="12">
      <line
        x1="2"
        y1="6"
        x2={PREVIEW_WIDTH - 2}
        y2="6"
        stroke={color}
        strokeWidth={weight}
        strokeDasharray={getDashArray(lineStyle, weight) || ''}
        strokeLinecap={getLineCap(lineStyle)}
      />
    </svg>
  );
}

function ColorSwatch({ name }) {
  const [field, , helpers] = useField(name);
  const inputRef = useRef(null);
  // Keep the latest setValue accessible from the native event listener
  // without re-binding the listener on every render.
  const setValueRef = useRef(helpers.setValue);
  setValueRef.current = helpers.setValue;

  // Bind to the native 'change' event so we only commit when the user
  // closes the picker. React's onChange maps to the 'input' event on
  // color inputs, which would fire continuously while dragging.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    const handler = (e) => setValueRef.current(e.target.value);
    el.addEventListener('change', handler);
    return () => el.removeEventListener('change', handler);
  }, []);

  // Sync external Formik updates (e.g. Restore defaults, hex input) back
  // into the uncontrolled color input.
  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== field.value) {
      inputRef.current.value = field.value;
    }
  }, [field.value]);

  return (
    <input
      ref={inputRef}
      type="color"
      defaultValue={field.value}
      style={COLOR_SWATCH_STYLE}
    />
  );
}

function HexInput({ name }) {
  const [field, , helpers] = useField(name);
  return (
    <Input
      size="mini"
      value={field.value}
      onChange={(_e, { value }) => {
        const v = value.startsWith('#') ? value : `#${value}`;
        helpers.setValue(HEX_COLOR_RE.test(v) ? v.toLowerCase() : value);
      }}
      maxLength={7}
      style={HEX_INPUT_STYLE}
    />
  );
}

function LineStyleDropdown({ name }) {
  const [field, , helpers] = useField(name);
  return (
    <Dropdown
      selection
      compact
      value={field.value}
      onChange={(_e, { value }) => helpers.setValue(value)}
      options={LINE_STYLE_OPTIONS}
      style={LINE_STYLE_DROPDOWN_STYLE}
    />
  );
}

function LineThicknessSlider({ name }) {
  const [field, , helpers] = useField(name);
  return (
    <Input
      type="range"
      min={LINE_THICKNESS_MIN}
      max={LINE_THICKNESS_MAX}
      step={LINE_THICKNESS_STEP}
      value={field.value}
      onChange={(_e, { value }) => helpers.setValue(Number(value))}
      style={THICKNESS_SLIDER_STYLE}
      title={`Line thickness: ${field.value}px`}
    />
  );
}

function SiteGraphicsRow({ label, colorName }) {
  const [colorField] = useField(`graphics.${colorName}`);
  return (
    <div style={GRAPHICS_ROW_STYLE}>
      <div
        style={{
          width: PREVIEW_WIDTH,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <SitePreview color={colorField.value} />
      </div>
      <div style={{ width: '180px' }}>{label}</div>
      <ColorSwatch name={`graphics.${colorName}`} />
      <HexInput name={`graphics.${colorName}`} />
      <div style={LINE_STYLE_DROPDOWN_STYLE} />
      <div style={THICKNESS_SLIDER_STYLE} />
    </div>
  );
}

function LinkGraphicsRow({ label, colorName, lineStyleName, thicknessName }) {
  const [colorField] = useField(`graphics.${colorName}`);
  const [lineStyleField] = useField(`graphics.${lineStyleName}`);
  const [thicknessField] = useField(`graphics.${thicknessName}`);
  return (
    <div style={GRAPHICS_ROW_STYLE}>
      <div
        style={{
          width: PREVIEW_WIDTH,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <LinePreview
          color={colorField.value}
          lineStyle={lineStyleField.value}
          weight={thicknessField.value}
        />
      </div>
      <div style={{ width: '180px' }}>{label}</div>
      <ColorSwatch name={`graphics.${colorName}`} />
      <HexInput name={`graphics.${colorName}`} />
      <LineStyleDropdown name={`graphics.${lineStyleName}`} />
      <LineThicknessSlider name={`graphics.${thicknessName}`} />
    </div>
  );
}

// Lifts Formik's isValid out of the render prop so the Modal.Actions buttons
// (which live outside the <Formik> tree) can reactively disable the submit
// button.
function FormStateSync({ onValidChange }) {
  const { isValid } = useFormikContext();
  useEffect(() => {
    onValidChange(isValid);
  }, [isValid, onValidChange]);
  return null;
}

function GraphicsPanel({ formatMessage }) {
  const { setFieldValue } = useFormikContext();
  const restoreDefaults = () => {
    Object.entries(DEFAULT_GRAPHICS_PREFS).forEach(([k, v]) => {
      setFieldValue(`graphics.${k}`, v);
    });
  };
  return (
    <>
      <SiteGraphicsRow
        label={formatMessage(additionalMessages.networkSiteColor)}
        colorName="networkSiteColor"
      />
      <SiteGraphicsRow
        label={formatMessage(additionalMessages.subscriberSiteColor)}
        colorName="subscriberSiteColor"
      />
      <SiteGraphicsRow
        label={formatMessage(additionalMessages.accessPointColor)}
        colorName="accessPointColor"
      />
      <LinkGraphicsRow
        label={formatMessage(additionalMessages.ptpLinkColor)}
        colorName="ptpLinkColor"
        lineStyleName="ptpLinkLineStyle"
        thicknessName="ptpLinkLineThickness"
      />
      <LinkGraphicsRow
        label={formatMessage(additionalMessages.meshLinkColor)}
        colorName="meshLinkColor"
        lineStyleName="meshLinkLineStyle"
        thicknessName="meshLinkLineThickness"
      />
      <LinkGraphicsRow
        label={formatMessage(additionalMessages.pmpLinkColor)}
        colorName="pmpLinkColor"
        lineStyleName="pmpLinkLineStyle"
        thicknessName="pmpLinkLineThickness"
      />
      <div style={{ textAlign: 'right', marginTop: '12px' }}>
        <Button type="button" onClick={restoreDefaults}>
          {formatMessage(additionalMessages.restoreDefaults)}
        </Button>
      </div>
    </>
  );
}

function Preferences(props) {
  const [open, setOpen] = useState(true);
  const [updatingPreferences, setUpdatingPreferences] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [activePanelKey, setActivePanelKey] = useState('PersonalInformation');
  const [isFormValid, setIsFormValid] = useState(true);
  const {
    firstName,
    lastName,
    email,
    phone,
    language,
    companyName,
    optIn,
    prefs,
    showHelp,
  } = useSelector((state) => state.mainFrame, shallowEqual);
  const initialValues = {
    firstName: firstName,
    lastName: lastName,
    email: email,
    language: language,
    mapRegion: localStorage.getItem('mapRegion') || 'us',
    companyName: companyName,
    phone: phone,
    optOut: !optIn,
    rangeUnits: prefs.rangeUnits,
    heightUnits: prefs.heightUnits,
    latLngFormat: prefs.latLngFormat,
    macAddressFormat: prefs.macAddressFormat,
    bom: prefs.bom,
    graphics: { ...DEFAULT_GRAPHICS_PREFS, ...(prefs.graphics || {}) },
  };

  const [showReloadMsg, setShowReloadMsg] = useState(false);

  const { formatMessage } = useIntl();
  const location = useLocation();
  const isAfc = location.pathname === '/afc';

  const onCloseHandler = () => {
    setOpen(false);
    props.onClose();
  };

  const onSubmit = (values) => {
    trimObject(values);
    const snakeVals = convertObjectKeys(values, toSnake);
    snakeVals.opt_in = !snakeVals.opt_out;
    setUpdatingPreferences(true);
    updatePreferencesApi(snakeVals)
      .then((res) => {
        setUpdatingPreferences(false);
        if (res) {
          store.dispatch(
            updatePreferences({ ...values, optIn: !values.optOut })
          );

          // update the map region
          if (values.mapRegion !== localStorage.getItem('mapRegion')) {
            localStorage.setItem('mapRegion', values.mapRegion);
            window.location.reload();
            return;
          }

          // re-apply feature styles so changes to graphics prefs
          // take effect without a page reload
          getCanvas()?.refreshFeatureStyles?.();
        }
        onCloseHandler();
      })
      .catch((err) => {
        setUpdatingPreferences(false);
        setErrMsg(err.message);
        console.error('Error while updating preferences', err);
      });
  };
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .trim()
      .required(
        formatMessage(additionalMessages.requiredFieldError, {
          fieldName: 'first name',
        })
      ),
    lastName: Yup.string()
      .trim()
      .required(
        formatMessage(additionalMessages.requiredFieldError, {
          fieldName: 'last name',
        })
      ),
    email: Yup.string()
      .email(
        formatMessage(additionalMessages.validFieldError, {
          fieldName: 'Email',
        })
      )
      .required(
        formatMessage(additionalMessages.requiredFieldError, {
          fieldName: 'Email',
        })
      ),
  });

  const panels = [
    {
      key: 'PersonalInformation',
      icon: '👤',
      titleProps: {
        content: formatMessage(additionalMessages.personalInformation),
      },
      contentProps: {
        content: (
          <>
            <Form.Group widths="equal">
              <SemanticField
                label={formatMessage(additionalMessages.firstName)}
                component={Form.Input}
                checkPermissions={false}
                id="firstName"
                name="firstName"
              ></SemanticField>
              <SemanticField
                label={formatMessage(additionalMessages.lastName)}
                component={Form.Input}
                id="lastName"
                name="lastName"
                checkPermissions={false}
              ></SemanticField>
            </Form.Group>
            <Form.Group widths="equal">
              <SemanticField
                label={formatMessage(additionalMessages.email)}
                component={Form.Input}
                type="email"
                checkPermissions={false}
                id="email"
                name="email"
                disabled
              ></SemanticField>
              <SemanticField
                label={formatMessage(additionalMessages.contact)}
                component={Form.Input}
                id="phone"
                name="phone"
                checkPermissions={false}
              ></SemanticField>
            </Form.Group>
            <Form.Group widths="equal">
              <SemanticField
                label={formatMessage(additionalMessages.companyName)}
                component={Form.Input}
                id="companyName"
                name="companyName"
                checkPermissions={false}
              ></SemanticField>
              <SemanticField
                component={Form.Select}
                label={formatMessage(additionalMessages.language)}
                options={LANGUAGES_PREFERENCES}
                name="language"
                checkPermissions={false}
                placeholder={formatMessage(additionalMessages.language)}
              />
              <SemanticField
                component={Form.Select}
                label="Map Region"
                options={Object.entries(MAP_REGIONS).map(([key, value]) => ({
                  key: key,
                  text: value,
                  value: key,
                }))}
                id="mapRegion"
                name="mapRegion"
                checkPermissions={false}
                placeholder="Map Region"
                onChange={() => setShowReloadMsg(true)}
              />
            </Form.Group>
            <SemanticField
              component={Form.Checkbox}
              label={formatMessage(additionalMessages.promotionalMails)}
              name="optOut"
              checkPermissions={false}
            />
            <Form.Checkbox
              label={formatMessage(additionalMessages.showHelpPopups)}
              defaultChecked={showHelp}
              onChange={() => store.dispatch(uiToggleHelpPopups())}
            />
          </>
        ),
      },
    },
    {
      key: 'UnitPreferences',
      icon: '📏',
      titleProps: {
        content: formatMessage(additionalMessages.unitsAndFormatting),
      },
      contentProps: {
        content: (
          <>
            <Form.Group widths="equal">
              <SemanticField
                component={Form.Select}
                label={formatMessage(additionalMessages.height)}
                options={HEIGHT_OPTIONS}
                name="heightUnits"
                checkPermissions={false}
                placeholder={formatMessage(additionalMessages.height)}
              />
              <SemanticField
                fluid
                component={Form.Select}
                label={formatMessage(additionalMessages.length)}
                options={LENGTH_OPTIONS}
                checkPermissions={false}
                name="rangeUnits"
                placeholder={formatMessage(additionalMessages.length)}
              />
            </Form.Group>
            <Form.Group widths="equal">
              <SemanticField
                fluid
                component={Form.Select}
                name="latLngFormat"
                checkPermissions={false}
                label={formatMessage(additionalMessages.latitudeLongitude)}
                options={LAT_LNG_OPTIONS}
                placeholder={formatMessage(
                  additionalMessages.latitudeLongitude
                )}
              />
              <SemanticField
                fluid
                component={Form.Select}
                name="macAddressFormat"
                checkPermissions={false}
                label={formatMessage(additionalMessages.macAddress)}
                options={MAC_ADDRESS_OPTIONS}
                placeholder={formatMessage(additionalMessages.macAddress)}
              />
            </Form.Group>
          </>
        ),
      },
    },
  ];

  if (!isAfc) {
    panels.push({
      key: 'BOMPreferences',
      icon: '📋',
      titleProps: {
        content: formatMessage(additionalMessages.bom),
      },
      contentProps: {
        content: (
          <>
            <OrderableCheckListControl label="Power Leads" />
          </>
        ),
      },
    });

    panels.push({
      key: 'GraphicsPreferences',
      icon: '🎨',
      titleProps: {
        content: formatMessage(additionalMessages.graphics),
      },
      contentProps: {
        content: <GraphicsPanel formatMessage={formatMessage} />,
      },
    });
  }

  return (
    <Fragment>
      <Modal onClose={onCloseHandler} onOpen={() => setOpen(true)} open={open}>
        <Modal.Header>
          {formatMessage(additionalMessages.accountAndPrefs)}
          <Button
            circular
            icon="close"
            title="Close"
            floated="right"
            onClick={onCloseHandler}
          />
        </Modal.Header>
        <Modal.Content>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            validateOnMount
          >
            {() => {
              return (
                <FormFormik id="account-prefs-form" className="ui form">
                  <FormStateSync onValidChange={setIsFormValid} />
                  <div style={{ display: 'flex', gap: '1.5rem' }}>
                    <Menu
                      vertical
                      pointing
                      secondary
                      style={{ width: '210px', flexShrink: 0 }}
                    >
                      {panels.map((p) => (
                        <Menu.Item
                          key={p.key}
                          active={activePanelKey === p.key}
                          onClick={() => setActivePanelKey(p.key)}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75em',
                            }}
                          >
                            <span>{p.titleProps.content}</span>
                            {p.icon && (
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: '1.18em',
                                  textAlign: 'center',
                                  flexShrink: 0,
                                  marginLeft: 'auto',
                                }}
                              >
                                {p.icon.charCodeAt(0) > 127 ? (
                                  p.icon
                                ) : (
                                  <Icon name={p.icon} style={{ margin: 0 }} />
                                )}
                              </span>
                            )}
                          </span>
                        </Menu.Item>
                      ))}
                    </Menu>
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        height: '330px',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {panels.map((p) => (
                        <div
                          key={p.key}
                          style={{
                            display: activePanelKey === p.key ? 'flex' : 'none',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                          }}
                        >
                          {p.contentProps.content}
                        </div>
                      ))}
                    </div>
                  </div>
                </FormFormik>
              );
            }}
          </Formik>
        </Modal.Content>
        {errMsg !== '' && (
          <Message negative attached>
            <Message.Header>
              {formatMessage(additionalMessages.preferencesError)}
            </Message.Header>
            <p>{errMsg}</p>
          </Message>
        )}
        <Modal.Actions>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ flex: '1 1 0%', textAlign: 'left' }}>
              {showReloadMsg && (
                <>Changing the map region will cause the page to reload.</>
              )}
            </div>
            <Button onClick={onCloseHandler}>
              {formatMessage(additionalMessages.cancel)}
            </Button>
            <Button
              type="submit"
              form="account-prefs-form"
              disabled={!isFormValid}
              loading={updatingPreferences}
              color="blue"
            >
              {formatMessage(additionalMessages.ok)}
            </Button>
          </div>
        </Modal.Actions>
      </Modal>
    </Fragment>
  );
}

export default Preferences;
